import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

/**
 * Host-agnostic Kirigami button. Renders a styled `<button>` with
 * color (`primary` | `secondary` | `danger`), size (`small` | `normal` | `compact`),
 * and variant (`filled` | `outlined`) flags. Supports a `loading` spinner and
 * `[k-icon-start]` / `[k-icon-end]` content-projected icon slots. Emits `onClick`
 * unless `disabled` or `loading`. `label` is optional so the component can render
 * icon-only buttons; in that case provide `ariaLabel` for accessibility.
 */
@Component({
  selector: 'kirigami-button',
  standalone: true,
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kirigami-button.html',
  styleUrls: ['./kirigami-button.scss'],
  host: {
    class: 'inline-block',
  },
})
export class KirigamiButton {
  @Input() type = 'button';
  @Input() label?: string;
  @Input() filled = false;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() color: 'primary' | 'secondary' | 'danger' = 'primary';
  @Input() size: 'small' | 'normal' | 'compact' = 'normal';
  @Input() styleClass!: string;
  @Input() ariaLabel!: string;

  @Output() onClick = new EventEmitter<Event>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();

  @HostListener('click', ['$event'])
  handleClick(event: Event) {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.onClick.emit(event);
  }

  buttonClass() {
    return {
      'k-filled': this.filled,
      'k-outlined': !this.filled,
      'k-disabled': this.disabled,
      'k-loading': this.loading,
      'k-normal': this.size === 'normal',
      'k-small': this.size === 'small',
      'k-compact': this.size === 'compact',
      'k-primary': this.color === 'primary',
      'k-secondary': this.color === 'secondary',
      'k-danger': this.color === 'danger',
    };
  }
}
